/**
 * I pezzi in comune fra Fatture e Passività: la scelta dell'anno di
 * competenza, i riquadri dei totali e i tasti colorati che filtrano un tipo
 * di documento. Cosi' le due pagine hanno lo stesso aspetto.
 */
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/cn';
import type { AnnoScelto } from '../../lib/fatture/elencoFatture';
import { COLORI, type Colore } from './colori';

interface SceltaAnnoProps {
  id: string;
  anno: AnnoScelto;
  anni: number[];
  onCambia: (anno: AnnoScelto) => void;
}

export function SceltaAnno({ id, anno, anni, onCambia }: SceltaAnnoProps) {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="text-sm font-medium text-gray-700">Anno di competenza</Label>
      <Select value={String(anno)} onValueChange={(v) => onCambia(v === 'tutti' ? 'tutti' : Number(v))}>
        <SelectTrigger id={id} className="h-9 w-40 bg-white border-gray-200"><SelectValue /></SelectTrigger>
        <SelectContent>
          {anni.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}
          <SelectItem value="tutti">Tutti gli anni</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

interface RiquadroTotaleProps {
  etichetta: string;
  icona: LucideIcon;
  coloreIcona: string;
  valore: string;
  nota: string;
  /** Il riquadro principale, scuro e piu' grande. */
  scuro?: boolean;
  className?: string;
  onClick?: () => void;
  title?: string;
}

export function RiquadroTotale({
  etichetta, icona: Icona, coloreIcona, valore, nota, scuro = false, className, onClick, title,
}: RiquadroTotaleProps) {
  return (
    <Card
      className={cn(scuro ? 'border-slate-900 bg-slate-900 text-white' : 'border-gray-200 bg-white', className)}
      onClick={onClick}
      title={title}
    >
      <CardContent className="p-3 md:p-4">
        <div className="mb-1 flex items-center justify-between">
          <p className={cn('text-[11px] font-semibold uppercase tracking-wider', scuro ? 'text-slate-300' : 'text-neutral-500')}>
            {etichetta}
          </p>
          <Icona className={cn('h-4 w-4', coloreIcona)} />
        </div>
        <div className={cn('truncate font-bold tabular-nums', scuro ? 'text-xl md:text-2xl' : 'text-base text-gray-900 md:text-xl')}>
          {valore}
        </div>
        <p className={cn('mt-1 text-[11px]', scuro ? 'text-slate-300' : 'text-neutral-500')}>{nota}</p>
      </CardContent>
    </Card>
  );
}

interface TastoTipoProps {
  colore: Colore;
  etichetta: string;
  acceso: boolean;
  onCambia: () => void;
}

/** Un clic mostra solo quel tipo, un altro clic torna a tutti. */
export function TastoTipo({ colore, etichetta, acceso, onCambia }: TastoTipoProps) {
  return (
    <button
      type="button"
      aria-pressed={acceso}
      title={acceso ? 'Torna a tutti i documenti' : `Mostra solo: ${etichetta.toLowerCase()}`}
      onClick={onCambia}
      className={cn(
        'h-9 rounded-full border-2 px-4 text-sm font-semibold whitespace-nowrap transition-colors',
        acceso ? COLORI[colore].acceso : COLORI[colore].spento
      )}
    >
      {etichetta}
    </button>
  );
}
